import { useFloatingNodeId, useFloating, useClick, useDismiss, useRole, useInteractions } from '@floating-ui/react';
import React__default, { useMemo } from 'react';
import { ANIMATION_DURATION_MS, CLOSE_MODAL_MESSAGE } from './constants.js';
import '../../utils/index.js';
import { useListen } from '../../utils/use_listen.js';
import { useControlledState } from '../../utils/controlled_state.js';
import { usePreferredTransitionStatus } from '../../utils/preferred_transition_status.js';
import { useCachedProp } from '../../utils/cached_prop.js';

const useModal = ({ open: controlledOpen, onToggle, id: propId, loading: propLoading = false, closeOnOutsidePress = true, closeListener = true, size: propSize = 'small', role: propRole = 'dialog', eventComponentName = 'modal', referenceElement, outsidePress, }) => {
    const nodeId = useFloatingNodeId();
    const id = propId ?? nodeId;
    const [open, setOpen] = useControlledState({
        defaultValue: false,
        value: controlledOpen,
        onChange: onToggle,
    });
    const data = useFloating({
        nodeId,
        open,
        onOpenChange: setOpen,
    });
    const { context, refs } = data;
    const { isMounted, status: transitionStatus } = usePreferredTransitionStatus(context, {
        duration: ANIMATION_DURATION_MS,
    });
    const outsidePressHandler = React__default.useCallback((event) => {
        if (outsidePress)
            return outsidePress(event);
        return closeOnOutsidePress;
    }, [outsidePress, closeOnOutsidePress]);
    const click = useClick(context, {
        toggle: true,
    });
    const dismiss = useDismiss(context, {
        bubbles: {
            escapeKey: false,
            outsidePress: false,
        },
        outsidePress: outsidePressHandler,
        outsidePressEvent: 'mousedown',
    });
    const role = useRole(context, {
        role: propRole,
    });
    const interactions = useInteractions([click, dismiss, role]);
    // Cache props so their update would be discarded when transitioning out
    const loading = useCachedProp({ prop: propLoading, shouldUpdate: open });
    const size = useCachedProp({ prop: propSize, shouldUpdate: open });
    React__default.useLayoutEffect(() => {
        if (referenceElement) {
            refs.setReference(referenceElement);
        }
    }, [refs, referenceElement]);
    // Listen to window message event and the close modal when needed.
    // This is a way to close all modals at once. Disabled by default.
    const config = useMemo(() => {
        return {
            enabled: closeListener,
            messages: [
                {
                    message: CLOSE_MODAL_MESSAGE,
                    cb: () => setOpen(false),
                },
            ],
        };
    }, [setOpen, closeListener]);
    useListen(config);
    React__default.useEffect(() => {
        // wait for ref to be filled
        queueMicrotask(() => {
            if (refs.floating.current) {
                const event = new CustomEvent(`common-ui:${eventComponentName}.toggle`, {
                    bubbles: true,
                    detail: { open, id },
                });
                refs.floating.current.dispatchEvent(event);
            }
        });
    }, [open, id, refs.floating, eventComponentName]);
    return React__default.useMemo(() => ({
        id,
        nodeId,
        open,
        setOpen,
        ...interactions,
        ...data,
        isMounted,
        transitionStatus,
        loading,
        size,
    }), [id, nodeId, open, setOpen, interactions, data, isMounted, transitionStatus, loading, size]);
};

export { useModal };
